import { BankMutation, CreateBankTransactionModel, TransactionType } from '../types/bankAccount';
import { Connection } from '../redux/slices/connectionsSlice';

export function buildCombinedTransactionModels(
  confirmedMatches: { transaction: CreateBankTransactionModel; matchedConnection: Connection; contributionPlanId?: number }[],
  selectedGroupMutations: CreateBankTransactionModel[],
): CreateBankTransactionModel[] {
  // create the confirmed matches as CreateBankTransactionModel
  const matchedModels: CreateBankTransactionModel[] = confirmedMatches.map(
    ({ transaction, matchedConnection, contributionPlanId }) => ({
      ...transaction,
      transactionType: contributionPlanId 
        ? TransactionType.CONTRIBUTION 
        : (transaction.amount < 0 ? TransactionType.PERSONAL_EXPENSE : TransactionType.PERSONAL_INCOME),
      participations: contributionPlanId ? undefined : [
        {
          connectionId: +matchedConnection.id,
          amount: transaction.amount,
        },
      ],
      contributionPayment: contributionPlanId ? {
        contributionPlanId,
        connectionId: +matchedConnection.id,
      } : undefined,
    }),
  );

  // create the selectedGroupMutataions as group transactions
  const unmatchedSplitModels: CreateBankTransactionModel[] = selectedGroupMutations.map(
    (transaction) => {
      return {
        ...transaction,
        participations: undefined, // no participations for group transactions
        contributionPayment: undefined,
        transactionType:
          transaction.amount < 0 ? TransactionType.GROUP_EXPENSE : TransactionType.GROUP_INCOME,
      };
    },
  );

  console.log('matchedModels', matchedModels);
  console.log('unmatchedSplitModels', unmatchedSplitModels);

  return [...matchedModels, ...unmatchedSplitModels];
}

export function matchMutationsToConnections(
  mutations: BankMutation[],
  connections: Connection[],
): Array<{
  transaction: CreateBankTransactionModel;
  matchedConnection?: Connection;
}> {
  const connectionNames = connections.map((c) => ({
    firstName: c.fields.FIRST_NAME,
    lastName: c.fields.LAST_NAME,
    connection: c,
  }));

  return mutations.map((t) => {
    const matchesBasedOnLastName = connectionNames.filter((c) => {
      const lastNameNormalized = c.lastName.toLowerCase().trim();
      return t.oppositeName.toLowerCase().trim().includes(lastNameNormalized);
    });

    const transactionModel = buildTransactionFromMutation(t);

    if (matchesBasedOnLastName.length === 0)
      return { transaction: transactionModel, matchedConnection: undefined };
    if (matchesBasedOnLastName.length === 1)
      return {
        transaction: transactionModel,
        matchedConnection: matchesBasedOnLastName[0].connection,
      };

    const matchesBasedOnFirstName = matchesBasedOnLastName.filter((c) => {
      const firstNameNormalized = c.firstName.toLowerCase().trim();
      return (
        t.oppositeName.toLowerCase().trim().includes(firstNameNormalized) ||
        t.oppositeName
          .toLowerCase()
          .trim()
          .split(/\s+/)
          .some((token) => token.at(0) === firstNameNormalized.at(0))
      );
    });

    if (matchesBasedOnFirstName.length === 0)
      return {
        transaction: transactionModel,
        matchedConnection: matchesBasedOnLastName[0].connection,
      };

    return {
      transaction: transactionModel,
      matchedConnection: matchesBasedOnFirstName[0].connection,
    };
  });
}

// export async function updateTransactionWithSplits(
//   tns: BankTransaction | null,
//   participations: BankTransactionParticipation[],
// ): Promise<BankTransaction> {
//   if (!tns) return Promise.reject(new Error('Transaction is null or undefined'));

//   const newTns = {
//     ...tns,
//     participations: participations.map((p) => ({
//       ...p,
//       amount: Math.floor(p.amount),
//     })),
//   };

//   await bankAccountService.updateBankTransaction(tns.transactionId, newTns);

//   return newTns;
// }

export function buildTransactionFromMutation(mutation: BankMutation): CreateBankTransactionModel {
  const transactionType =
    mutation.amount >= 0 ? TransactionType.GROUP_INCOME : TransactionType.GROUP_EXPENSE;

  return {
    amount: mutation.amount,
    description: `${mutation.oppositeName}, ${mutation.description}`,
    date: mutation.transactionDate,
    mutationId: mutation.mutationId,
    transactionType,
  };
}

export function formatMoney(amount: number, locale: string, currency: string): string {
  if (amount === null || amount === undefined) return '';

  const options = {
    style: 'currency' as const,
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  return new Intl.NumberFormat(locale, options).format(Math.abs(amount));
}

export function formatMoneyWithSign(
  amount: number,
  locale: string,
  currency: string,
  showPlus: boolean = true,
): string {
  if (amount === null || amount === undefined) return '';

  const baseFormatted = formatMoney(Math.abs(amount), locale, currency);

  if (amount > 0) return showPlus ? `+${baseFormatted}` : baseFormatted;
  if (amount < 0) return `−${baseFormatted}`;
  return baseFormatted; // No sign for 0
}

export function formatEuroCents(amount: number, locale: string): string {
  return formatMoney(amount / 100, locale, 'EUR');
}

export function formatEuroCentsWithSign(
  amount: number,
  locale: string,
  showPlus: boolean = true,
): string {
  return formatMoneyWithSign(amount / 100, locale, 'EUR', showPlus);
}
